import { MENU_ROLES, type MenuConfig, type MenuRole } from "@repo/types"
import { effectiveMenuRoles, LOCKED_MENU_HREFS, type MenuItem } from "../../components/sidebar-menu"

/** Brouillon de l'écran « Menus par rôle » : rôles cochés pour chaque menu (clé = lien du menu). */
export type MenuDraft = Record<string, MenuRole[]>

const knownRoles = (roles: readonly string[] | undefined): MenuRole[] =>
  (roles ?? []).filter((role): role is MenuRole => (MENU_ROLES as readonly string[]).includes(role))

export const flattenMenuItems = (items: MenuItem[]): MenuItem[] =>
  items.flatMap((item) => [item, ...(item.subMenu ?? [])])

/** Rôles cochés au départ : la configuration enregistrée, sinon les rôles par défaut du menu. */
export function buildMenuDraft(items: MenuItem[], config: MenuConfig | undefined): MenuDraft {
  return Object.fromEntries(flattenMenuItems(items).map((item) => [item.href, knownRoles(effectiveMenuRoles(item, config))]))
}

const sameRoles = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((role) => b.includes(role))

/**
 * Configuration à enregistrer : seuls les menus dont les rôles diffèrent des valeurs par défaut.
 * Un menu revenu à ses rôles par défaut sort de la configuration et suivra les évolutions du code.
 */
export function buildMenuConfigFromDraft(items: MenuItem[], draft: MenuDraft): MenuConfig {
  const config: MenuConfig = {}
  for (const item of flattenMenuItems(items)) {
    if (LOCKED_MENU_HREFS.has(item.href)) continue
    const roles = draft[item.href]
    if (!roles) continue
    if (!sameRoles(roles, knownRoles(item.roles))) config[item.href] = MENU_ROLES.filter((role) => roles.includes(role))
  }
  return config
}

export function toggleDraftRole(draft: MenuDraft, href: string, role: MenuRole, checked: boolean): MenuDraft {
  const current = draft[href] ?? []
  const next = checked ? [...new Set([...current, role])] : current.filter((r) => r !== role)
  return { ...draft, [href]: next }
}
